import type {
  AbstractTemplateInOut,
  ConfigurationExpressionValueInOut,
  ConfigurationFunctionValueInOut,
  EnumeratedValueFrameInOut,
  RangedValueFrameInOut,
  SimpleValueSetupInOut,
  SubTemplateInOut,
  SubTemplateValueSetupInOut,
  TemplateInOut,
  TemplatePropertyInOut,
  TemplateVariantInOut,
  ValidationInOut,
  ValueFrameInOut,
  ValueSetupInOut,
} from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'

import {
  type AbstractTemplate,
  type ConfigurationFieldExpressionValue,
  ConfigurationFieldType,
  type ConfigurationTemplateFunctionValue,
  isEnumeratedValueFrameFieldValue,
  isRangedValueFrameFieldValue,
  type SimpleValueSetupFieldValue,
  type SimpleValueSetupFrameValue,
  type SubTemplateFieldValue,
  type SubTemplateValueSetupFieldValue,
  type TemplateField,
  type TemplateFunctions,
  type TemplateReference,
  type TemplateReferenceValueSetupFieldValue,
  type ValidationFieldValue,
  type ValueSetupFieldValue,
} from '../../types/templateModel'
import { optionallyPick, optionallyPickAndMap } from '../helpers'
import { isNotInherited } from '../templateOperations/types'
import type { Enriched } from '../types'

const toValueSetup = (
  fromValue: ValueSetupFieldValue,
): Omit<ValueSetupInOut, '_type'> => {
  const pickedValidations = optionallyPickAndMap({
    originalObject: fromValue,
    key: 'validations',
    mapFn: (validations) =>
      validations.filter(isNotInherited).map(toValidation),
    path: null,
  })

  return {
    ...optionallyPickAndMap({
      originalObject: fromValue,
      key: 'documentation',
      asKey: 'documentation',
      mapFn: (field) => field.value,
      path: `${fromValue.path}.documentation`,
    }),
    ...optionallyPickAndMap({
      originalObject: fromValue,
      key: 'required',
      mapFn: toConfigurationExpressionValue,
      path: fromValue.required?.path,
    }),
    ...optionallyPickAndMap({
      originalObject: fromValue,
      key: 'requiredInModelInstance',
      mapFn: toConfigurationExpressionValue,
      path: fromValue.requiredInModelInstance?.path,
    }),
    ...optionallyPickAndMap({
      originalObject: fromValue,
      key: 'valueConfig',
      asKey: 'valueConfig',
      mapFn: (config) => config.value,
      path: `${fromValue.path}.valueConfig`,
    }),
    ...(pickedValidations?.['validations']?.length ? pickedValidations : {}),
  }
}

const toSimpleValueSetup = (
  value: SimpleValueSetupFieldValue,
): SimpleValueSetupInOut => {
  const pickedDynamicFunctions = optionallyPickAndMap({
    originalObject: value,
    key: 'dynamicFunctions',
    mapFn: (functions) =>
      functions.filter(isNotInherited).map(toConfigurationFunctionValue),
    path: null,
  })

  return {
    _type: 'SimpleValueSetupInOut',
    ...optionallyPickAndMap({
      originalObject: value,
      key: 'defaultValue',
      mapFn: toConfigurationExpressionValue,
      path: value.defaultValue?.path,
    }),
    ...optionallyPickAndMap({
      originalObject: value,
      key: 'frame',
      mapFn: toValueFrame,
      fieldOriginMapper: (frame) =>
        !!frame && isEnumeratedValueFrameFieldValue(frame)
          ? frame.entries?.origin
          : frame?.range?.origin,
      path:
        value.frame && isEnumeratedValueFrameFieldValue(value.frame)
          ? value.frame.entries?.path
          : value.frame?.range?.path,
    }),
    ...(pickedDynamicFunctions?.['dynamicFunctions']?.length
      ? pickedDynamicFunctions
      : {}),
    ...toValueSetup(value),
  }
}

const toValidation = (validation: ValidationFieldValue): ValidationInOut => ({
  _type: 'ValidationInOut',
  ...optionallyPickAndMap({
    originalObject: validation,
    key: 'key',
    mapFn: toConfigurationExpressionValue,
    path: validation.key?.path,
  }),
  ...optionallyPickAndMap({
    originalObject: validation,
    key: 'keyArguments',
    mapFn: toConfigurationExpressionValue,
    path: validation.keyArguments?.path,
  }),
  ...optionallyPickAndMap({
    originalObject: validation,
    key: 'defaultPattern',
    mapFn: toConfigurationExpressionValue,
    path: validation.defaultPattern?.path,
  }),
  ...optionallyPickAndMap({
    originalObject: validation,
    key: 'validation',
    mapFn: toConfigurationExpressionValue,
    path: validation.validation?.path,
  }),
})

const toValueFrame = (
  frame: Optional<SimpleValueSetupFrameValue>,
): ValueFrameInOut | null => {
  if (!frame) {
    return null
  }

  if (isEnumeratedValueFrameFieldValue(frame)) {
    return {
      _type: 'EnumeratedValueFrameInOut',
      ...optionallyPickAndMap({
        originalObject: frame,
        key: 'entries',
        mapFn: toConfigurationExpressionValue,
        path: null,
      }),
    } as EnumeratedValueFrameInOut
  } else if (isRangedValueFrameFieldValue(frame)) {
    return {
      _type: 'RangedValueFrameInOut',
      ...optionallyPickAndMap({
        originalObject: frame,
        key: 'range',
        mapFn: toConfigurationExpressionValue,
        path: null,
      }),
    } as RangedValueFrameInOut
  } else {
    throw new Error(`Unsupported frame type for frame ${frame}`)
  }
}

const toConfigurationExpressionValue = (
  fromValue: ConfigurationFieldExpressionValue,
): Optional<ConfigurationExpressionValueInOut> =>
  fromValue['documentation'] || fromValue['expression']?.fplusSource
    ? {
        _type: 'ConfigurationExpressionValueInOut',
        ...optionallyPickAndMap({
          originalObject: fromValue,
          key: 'documentation',
          asKey: 'documentation',
          mapFn: (field) => field.value,
          path: `${fromValue.path}.documentation`,
        }),
        ...optionallyPickAndMap({
          originalObject: fromValue,
          key: 'expression',
          mapFn: (field) => field.fplusSource,
          path: fromValue.path,
        }),
      }
    : undefined

export const toConfigurationFunctionValue = (
  fromValue: ConfigurationTemplateFunctionValue,
): ConfigurationFunctionValueInOut => ({
  _type: 'ConfigurationFunctionValueInOut',
  ...optionallyPickAndMap({
    originalObject: fromValue,
    key: 'function',
    mapFn: (field) => field.fplusSource,
    path: fromValue.path,
    fieldOriginMapper: () => fromValue.origin,
  }),
  ...optionallyPickAndMap({
    originalObject: fromValue,
    key: 'documentation',
    asKey: 'documentation',
    mapFn: (field) => field.value,
    path: `${fromValue.path}.documentation`,
  }),
})

const toAbstractTemplate = (
  fromAbstractTemplate: AbstractTemplate,
): Omit<AbstractTemplateInOut, '_type'> => {
  const pickedDeclaredFunctions = optionallyPickAndMap({
    originalObject: fromAbstractTemplate,
    key: 'declaredFunctions',
    mapFn: (functions) =>
      functions
        .filter(isNotInherited)
        .map(toConfigurationFunctionValue)
        .filter(
          (declaredFunction) =>
            !!declaredFunction.function || !!declaredFunction.documentation,
        ),
    path: null,
  })

  const pickedDynamicFunctions = optionallyPickAndMap({
    originalObject: fromAbstractTemplate,
    key: 'dynamicFunctions',
    mapFn: (functions) =>
      functions.filter(isNotInherited).map(toConfigurationFunctionValue),
    path: null,
  })

  return {
    modelTypeName: fromAbstractTemplate.modelTypeName,
    templateTypeName: fromAbstractTemplate.typeName,
    ...(pickedDeclaredFunctions?.['declaredFunctions']?.length
      ? pickedDeclaredFunctions
      : {}),
    ...(pickedDynamicFunctions?.['dynamicFunctions']?.length
      ? pickedDynamicFunctions
      : {}),
    ...optionallyPickAndMap({
      originalObject: fromAbstractTemplate,
      key: 'documentation',
      asKey: 'documentation',
      mapFn: (field) => field.value,
      path: `${fromAbstractTemplate.path}.documentation`,
    }),
  }
}

export const toSubTemplate = (
  fromVariant: SubTemplateFieldValue,
): SubTemplateInOut => {
  const properties = toServerTemplateFields(fromVariant.fields)
  const pickedDeclaredFunctions = optionallyPickAndMap({
    originalObject: fromVariant,
    key: 'declaredFunctions',
    mapFn: (declaredFunctions) =>
      declaredFunctions
        .filter(
          (declaredFunction) =>
            isNotInherited(declaredFunction) &&
            declaredFunction.function?.fplusSource,
        )
        .map(toConfigurationFunctionValue),
    path: null,
  })
  const pickedDynamicFunctions = optionallyPickAndMap({
    originalObject: fromVariant,
    key: 'dynamicFunctions',
    mapFn: (dynamicFns) =>
      dynamicFns.filter(isNotInherited).map(toConfigurationFunctionValue),
    path: null,
  })

  return {
    _type: 'SubTemplateInOut',
    name: fromVariant.subTemplateIdentifier,
    ...toAbstractTemplate(fromVariant),
    ...optionallyPickAndMap({
      originalObject: fromVariant,
      key: 'defaultSelected',
      mapFn: toConfigurationExpressionValue,
      path: fromVariant.defaultSelected?.path,
    }),
    ...optionallyPickAndMap({
      originalObject: fromVariant,
      key: 'selectable',
      mapFn: toConfigurationExpressionValue,
      path: fromVariant.selectable?.path,
    }),
    ...optionallyPick({
      originalObject: fromVariant,
      key: 'subTemplateIdentifier',
      asKey: 'name',
      path: fromVariant.path,
    }),
    ...optionallyPickAndMap({
      originalObject: fromVariant,
      key: 'parent',
      mapFn: (variantParent) => variantParent.value,
      path: `${fromVariant.path}.parent`,
    }),
    ...(pickedDeclaredFunctions?.['declaredFunctions']?.length
      ? pickedDeclaredFunctions
      : {}),
    ...(pickedDynamicFunctions?.['dynamicFunctions']?.length
      ? pickedDynamicFunctions
      : {}),
    ...(properties.length ? { properties } : {}),
  }
}

export const toSubTemplateValueSetup = (
  fromValue: SubTemplateValueSetupFieldValue,
): SubTemplateValueSetupInOut => ({
  ...toValueSetup(fromValue),
  ...optionallyPickAndMap({
    originalObject: fromValue,
    key: 'variants',
    mapFn: (variants) => variants.filter(isNotInherited).map(toSubTemplate),
    path: null,
  }),
  ...optionallyPickAndMap({
    originalObject: fromValue,
    key: 'cardinality',
    mapFn: toConfigurationExpressionValue,
    path: fromValue.cardinality?.path,
  }),
  _type: 'SubTemplateValueSetupInOut',
})

const toTemplateVariant = (
  fromValue: TemplateReference,
): TemplateVariantInOut => ({
  _type: 'TemplateVariantInOut',
  ...optionallyPickAndMap({
    originalObject: fromValue,
    key: 'source',
    asKey: 'name',
    mapFn: (fromValue) => fromValue,
    path: fromValue.path,
  }),
  ...optionallyPickAndMap({
    originalObject: fromValue,
    key: 'defaultSelected',
    mapFn: toConfigurationExpressionValue,
    path: fromValue.defaultSelected?.path,
  }),
  ...optionallyPickAndMap({
    originalObject: fromValue,
    key: 'selectable',
    mapFn: toConfigurationExpressionValue,
    path: fromValue.selectable?.path,
  }),
  ...optionallyPickAndMap({
    originalObject: fromValue,
    key: 'cardinality',
    mapFn: toConfigurationExpressionValue,
    path: fromValue.cardinality?.path,
  }),
  ...optionallyPick({
    originalObject: fromValue,
    key: 'key',
    path: fromValue.path,
  }),
})

const toTemplateValueSetup = (
  fromValue: TemplateReferenceValueSetupFieldValue,
): SubTemplateValueSetupInOut => {
  const pickedVariants = optionallyPickAndMap({
    originalObject: fromValue,
    key: 'variants',
    mapFn: (variants) => variants.filter(isNotInherited).map(toTemplateVariant),
    path: null,
  })

  return {
    _type: 'TemplateValueSetupInOut',
    ...toValueSetup(fromValue),
    ...optionallyPickAndMap({
      originalObject: fromValue,
      key: 'cardinality',
      mapFn: toConfigurationExpressionValue,
      path: fromValue.cardinality?.path,
    }),
    ...(pickedVariants?.['variants']?.length ? pickedVariants : {}),
  }
}

export { toConfigurationExpressionValue, toSimpleValueSetup }

export const transformVisualModel = (field: TemplateField) => {
  assertNotEmpty(field.value)

  switch (field.fieldType) {
    case ConfigurationFieldType.SimpleValueSetup:
      return toSimpleValueSetup(field.value)
    case ConfigurationFieldType.SubTemplateValueSetup:
      return toSubTemplateValueSetup(field.value)
    case ConfigurationFieldType.TemplateValueSetup:
      return toTemplateValueSetup(field.value)
    case ConfigurationFieldType.ConfigurationExpressionValue:
      return toConfigurationExpressionValue(field.value)
  }
}

export const toServerTemplateFields = (
  fields: TemplateField[],
): TemplatePropertyInOut[] =>
  fields
    .filter((field) => isNotInherited(field))
    .map(toServerTemplateField)
    .filter(notEmpty)

export const toServerTemplateField = (field: TemplateField) => {
  if (!field.value) {
    return null
  }

  const fieldValueSetup = transformVisualModel(field)
  const keys = fieldValueSetup ? Object.keys(fieldValueSetup) : []

  if (!keys.length || (keys.length === 1 && keys[0] === '_type')) {
    return
  }

  return {
    _type: 'TemplatePropertyInOut',
    name: field.name,
    value: fieldValueSetup,
  } satisfies TemplatePropertyInOut
}

export const toServerTemplateFunctions = (functions: TemplateFunctions) => ({
  ...optionallyPickAndMap({
    originalObject: functions,
    key: 'declaredFunctions',
    mapFn: (functions) =>
      functions
        .filter(isNotInherited)
        .map(toConfigurationFunctionValue)
        .filter((declaredFunction) => !!declaredFunction.function),
    path: null,
  }),
  ...optionallyPickAndMap({
    originalObject: functions,
    key: 'dynamicFunctions',
    mapFn: (functions) =>
      functions.filter(isNotInherited).map(toConfigurationFunctionValue),
    path: null,
  }),
})

export const toServerTemplateWithoutFields = (
  template: Optional<Enriched<TemplateInOut>>,
): Optional<TemplateInOut> => {
  if (!template) {
    return template
  }

  return {
    ...optionallyPickAndMap({
      path: `{${template.name}}.documentation`,
      originalObject: template,
      key: 'documentation',
      asKey: 'documentation',
      mapFn: (field) => field.value,
    }),
    ...(
      [
        'packageName',
        'templateFromDate',
        'parent',
        'replaces',
        'valid',
        'name',
        'modelTypeName',
        'templateTypeName',
      ] as const
    ).reduce(
      (accTemplate, key) => ({
        ...accTemplate,
        ...optionallyPick({
          // This path is quite irrelevant since these fields are not inheritable and therefore don't have an origin in them that will prevent them from being mapped
          path: `{${template.name}}.${key}`,
          originalObject: template,
          key,
        }),
      }),
      {},
    ),
  } as TemplateInOut
}
